<template>
	<v-card>
		<v-toolbar flat color="dark" dark>
			<v-toolbar-title>
				<span class="pl-2">Communication – Activity</span>
			</v-toolbar-title>
			<v-spacer></v-spacer>
			<v-chip v-if="schoolyear" small color="orange darken-2" text-color="white">
				Actief schooljaar: {{ schoolyear.name }}
			</v-chip>
		</v-toolbar>

		<v-card-text>
			<v-alert v-if="loadError" type="error" dense outlined class="mb-4">
				{{ loadError }}
			</v-alert>
			<v-alert v-if="sendResult" :type="sendResult.success ? 'success' : 'warning'" dense outlined class="mb-4">
				{{ sendResult.msg }}
				<span v-if="sendCountsLabel"> ({{ sendCountsLabel }})</span>
				<div v-if="confirmationResultLabel" class="mt-1">{{ confirmationResultLabel }}</div>
			</v-alert>
			<v-alert v-if="!loading && !schoolyear" type="info" dense outlined class="mb-4">
				Er is geen actief schooljaar. Stel eerst een actief schooljaar in.
			</v-alert>

			<h3 class="subtitle-1 font-weight-medium mb-2">1. Selecteer een activiteit</h3>
			<v-text-field
				v-model="activitySearch"
				append-icon="mdi-magnify"
				label="Zoeken in planning"
				single-line
				hide-details
				dense
				class="mb-3"
				style="max-width: 360px"
			></v-text-field>
			<v-data-table
				:headers="activityHeaders"
				:items="activities"
				:search="activitySearch"
				:loading="loading"
				class="elevation-0 mb-6"
				@click:row="selectActivity"
			>
				<template v-slot:item.selected="{ item }">
					<v-btn icon small @click.stop="selectActivity(item)">
						<v-icon v-if="selectedPlanningId === item.id" color="primary">mdi-check-circle</v-icon>
						<v-icon v-else color="grey lighten-1">mdi-circle-outline</v-icon>
					</v-btn>
				</template>
				<template v-slot:item.color="{ item }">
					<span
						class="activity-swatch"
						:style="activitySwatchStyle(item.tbl_sys_schoolyear_planning_activity)"
						:title="item.activity_name"
					></span>
				</template>
				<template v-slot:item.audience="{ item }">
					<v-chip v-if="item.for_personnel" x-small class="mr-1" color="primary" text-color="white">
						Personeel
					</v-chip>
					<v-chip v-if="item.for_student" x-small color="blue-grey" text-color="white">
						Student
					</v-chip>
				</template>
				<template v-slot:item.start_date="{ item }">
					{{ formatDate(item.start_date) }}
				</template>
				<template v-slot:item.end_date="{ item }">
					{{ formatDate(item.end_date) }}
				</template>
				<template v-slot:item.time="{ item }">
					{{ item.time_range && item.time_range !== '—' ? item.time_range : "" }}
				</template>
			</v-data-table>

			<div v-if="selectedPlanning">
				<v-card outlined class="mb-6 selected-activity-card">
					<v-card-title class="subtitle-1 py-3">
						<span
							class="activity-swatch mr-2"
							:style="activitySwatchStyle(selectedPlanning.tbl_sys_schoolyear_planning_activity)"
						></span>
						{{ selectedPlanning.activity_name }}
					</v-card-title>
					<v-card-text class="pt-0">
						<div class="body-2">
							<strong>Datum:</strong> {{ selectedPlanning.date_range }}
							<span v-if="selectedPlanning.time_range && selectedPlanning.time_range !== '—'">
								&nbsp;·&nbsp;<strong>Tijd:</strong> {{ selectedPlanning.time_range }}
							</span>
							<span v-if="selectedPlanning.location">
								&nbsp;·&nbsp;<strong>Locatie:</strong> {{ selectedPlanning.location }}
							</span>
						</div>
					</v-card-text>
				</v-card>

				<v-alert
					v-if="audience && !audience.for_personnel && audience.for_student"
					type="info"
					dense
					outlined
					class="mb-4"
				>
					Deze activiteit is alleen bedoeld voor studenten. Studentcommunicatie is nog niet beschikbaar.
				</v-alert>
				<v-alert
					v-else-if="audience && !audience.for_personnel && !audience.for_student"
					type="warning"
					dense
					outlined
					class="mb-4"
				>
					Voor deze activiteit is geen doelgroep ingesteld bij de kleurcodering.
				</v-alert>

				<template v-if="audience && audience.for_personnel">
					<v-row>
						<v-col cols="12" md="7">
							<h3 class="subtitle-1 font-weight-medium mb-2">2. Selecteer ontvangers</h3>
							<p class="caption grey--text mb-2">
								Actieve medewerkers uit employee cohort van {{ schoolyear && schoolyear.name }}.
								E-mail gaat naar het OSBOD e-mailadres.
							</p>
							<v-text-field
								v-model="employeeSearch"
								append-icon="mdi-magnify"
								label="Zoeken in personeel"
								single-line
								hide-details
								dense
								class="mb-2"
							></v-text-field>
							<div class="d-flex flex-wrap align-center mb-2">
								<v-btn small text color="primary" class="mr-1" @click="selectAllWithEmail">
									Selecteer allen
								</v-btn>
								<v-btn small text @click="selectedEmployees = []">Deselecteer</v-btn>
								<v-spacer></v-spacer>
								<span class="caption grey--text">
									{{ selectedEmployees.length }} geselecteerd
									<span v-if="personnelWithoutEmail.length">
										· {{ personnelWithoutEmail.length }} zonder OSBOD e-mail
									</span>
								</span>
							</div>
							<v-data-table
								v-model="selectedEmployees"
								:headers="employeeHeaders"
								:items="personnelWithEmail"
								:search="employeeSearch"
								:loading="composeLoading"
								show-select
								item-key="id"
								class="elevation-0"
							>
								<template v-slot:item.osbod_email="{ item }">
									{{ item.osbod_email }}
								</template>
							</v-data-table>
							<v-expansion-panels v-if="personnelWithoutEmail.length" class="mt-3" accordion>
								<v-expansion-panel>
									<v-expansion-panel-header class="caption">
										Medewerkers zonder OSBOD e-mail ({{ personnelWithoutEmail.length }})
									</v-expansion-panel-header>
									<v-expansion-panel-content>
										<v-simple-table dense>
											<tbody>
												<tr v-for="person in personnelWithoutEmail" :key="person.id">
													<td>{{ person.display_name }}</td>
													<td>{{ person.function_name }}</td>
												</tr>
											</tbody>
										</v-simple-table>
									</v-expansion-panel-content>
								</v-expansion-panel>
							</v-expansion-panels>
						</v-col>
						<v-col cols="12" md="5">
							<h3 class="subtitle-1 font-weight-medium mb-2">3. Controleer en verstuur</h3>
							<v-card outlined>
								<v-card-subtitle class="pb-1">Onderwerp</v-card-subtitle>
								<v-card-text class="pt-0">
									<div class="body-2 font-weight-medium">{{ subject || "—" }}</div>
								</v-card-text>
								<v-divider></v-divider>
								<v-card-subtitle class="pb-1">Voorbeeld</v-card-subtitle>
								<iframe
									v-if="previewHtml"
									class="email-preview-frame"
									:srcdoc="previewHtml"
									sandbox=""
									title="E-mailvoorbeeld"
								></iframe>
								<div v-else class="pa-4 caption grey--text">
									Voorbeeld wordt geladen...
								</div>
								<v-card-actions>
									<v-spacer></v-spacer>
									<v-btn
										color="primary"
										depressed
										:disabled="!canSend"
										:loading="sending"
										@click="openConfirmDialog"
									>
										<v-icon left>mdi-email-send</v-icon>
										Verstuur e-mail
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-col>
					</v-row>
				</template>
			</div>
		</v-card-text>

		<v-dialog v-model="confirmDialog" max-width="760" scrollable>
			<v-card>
				<v-card-title class="headline">Bevestigingssamenvatting versturen</v-card-title>
				<v-card-text>
					<p class="mb-3">
						U staat op het punt
						<strong>{{ selectedPlanning && selectedPlanning.activity_name }}</strong>
						te mailen naar
						<strong>{{ selectedEmployees.length }}</strong>
						medewerker{{ selectedEmployees.length === 1 ? "" : "s" }}.
						Daarna wordt een bevestigingssamenvatting verstuurd met het voorbeeld van de mail en de lijst ontvangers.
					</p>
					<p class="subtitle-2 mb-1">Verstuur de bevestigingssamenvatting naar <span class="error--text">*</span></p>
					<p class="caption grey--text mb-2">
						Minimaal één adres is verplicht. Zo kan altijd worden gecontroleerd dat zowel de personeelsmail als de samenvatting zijn verstuurd.
					</p>
					<v-alert v-if="confirmError" type="error" dense outlined class="mb-3">
						{{ confirmError }}
					</v-alert>
					<v-checkbox
						v-for="option in confirmationRecipients"
						:key="option.email"
						v-model="selectedConfirmationEmails"
						:label="option.label + ' (' + option.email + ')'"
						:value="option.email"
						hide-details
						class="mt-0"
					></v-checkbox>
					<v-divider class="my-4"></v-divider>
					<p class="subtitle-2 mb-2">Ontvangers van de personeelsmail</p>
					<v-simple-table dense class="mb-4">
						<thead>
							<tr>
								<th class="text-left">Naam</th>
								<th class="text-left">OSBOD e-mail</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="person in selectedEmployees" :key="person.id">
								<td>{{ person.display_name }}</td>
								<td>{{ person.osbod_email }}</td>
							</tr>
						</tbody>
					</v-simple-table>
					<p class="subtitle-2 mb-2">Voorbeeld van de te versturen e-mail</p>
					<iframe
						v-if="previewHtml"
						class="email-preview-frame email-preview-frame--dialog"
						:srcdoc="previewHtml"
						sandbox=""
						title="E-mailvoorbeeld"
					></iframe>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text :disabled="sending" @click="confirmDialog = false">Annuleren</v-btn>
					<v-btn
						color="primary"
						depressed
						:disabled="!canConfirmSend"
						:loading="sending"
						@click="sendEmails"
					>
						Verstuur mail en samenvatting
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../../config";
import { activitySwatchStyle } from "../../constants/schoolyearPlanningActivities";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 180000
});

export default {
	name: "CommunicationActivity",
	data() {
		return {
			loading: false,
			composeLoading: false,
			sending: false,
			loadError: "",
			schoolyear: null,
			activities: [],
			activitySearch: "",
			employeeSearch: "",
			selectedPlanningId: null,
			selectedPlanning: null,
			audience: null,
			personnel: [],
			selectedEmployees: [],
			subject: "",
			previewHtml: "",
			confirmDialog: false,
			confirmError: "",
			sendResult: null,
			confirmationRecipients: [
				{ email: "directie@osbod.org", label: "Directie" },
				{ email: "helpdesk@osbod.org", label: "Helpdesk" },
				{ email: "admin-techniek@osbod.org", label: "Admin-techniek" }
			],
			selectedConfirmationEmails: [],
			activityHeaders: [
				{ text: "", value: "selected", sortable: false, width: 40 },
				{ text: "", value: "color", sortable: false, width: 48 },
				{ text: "Activiteit", value: "activity_name" },
				{ text: "Doelgroep", value: "audience", sortable: false },
				{ text: "Startdatum", value: "start_date" },
				{ text: "Einddatum", value: "end_date" },
				{ text: "Tijd", value: "time", sortable: false },
				{ text: "Locatie", value: "location" }
			],
			employeeHeaders: [
				{ text: "Naam", value: "display_name" },
				{ text: "Functie", value: "function_name" },
				{ text: "OSBOD e-mail", value: "osbod_email" }
			]
		};
	},
	computed: {
		personnelWithEmail() {
			return (this.personnel || []).filter(function(person) {
				return person.has_osbod_email;
			});
		},
		personnelWithoutEmail() {
			return (this.personnel || []).filter(function(person) {
				return !person.has_osbod_email;
			});
		},
		canSend() {
			return (
				this.selectedPlanning &&
				this.audience &&
				this.audience.for_personnel &&
				this.selectedEmployees.length > 0 &&
				!this.sending
			);
		},
		canConfirmSend() {
			return this.canSend && this.selectedConfirmationEmails.length > 0;
		},
		sendCountsLabel() {
			if (!this.sendResult) return "";
			var parts = [];
			if (this.sendResult.sent && this.sendResult.sent.length) {
				parts.push(this.sendResult.sent.length + " personeel verstuurd");
			}
			if (this.sendResult.failed && this.sendResult.failed.length) {
				parts.push(this.sendResult.failed.length + " personeel mislukt");
			}
			if (this.sendResult.skipped && this.sendResult.skipped.length) {
				parts.push(this.sendResult.skipped.length + " overgeslagen");
			}
			return parts.join(", ");
		},
		confirmationResultLabel() {
			if (!this.sendResult || !this.sendResult.confirmation) return "";
			var sent = this.sendResult.confirmation.sent || [];
			var failed = this.sendResult.confirmation.failed || [];
			var parts = [];
			if (sent.length) {
				parts.push("Samenvatting verstuurd naar " + sent.join(", "));
			}
			if (failed.length) {
				parts.push(
					"Samenvatting mislukt voor " +
						failed
							.map(function(item) {
								return item.email || item;
							})
							.join(", ")
				);
			}
			return parts.join(". ");
		}
	},
	created() {
		this.fetchSetup();
	},
	methods: {
		activitySwatchStyle: activitySwatchStyle,
		currentUserId() {
			try {
				var user = JSON.parse(localStorage.getItem("user") || "null");
				return user && user.id ? user.id : null;
			} catch (e) {
				return null;
			}
		},
		formatDate(value) {
			if (!value) return "";
			var s = String(value).slice(0, 10);
			var parts = s.split("-");
			if (parts.length === 3) {
				return parts[2] + "-" + parts[1] + "-" + parts[0];
			}
			return s;
		},
		selectAllWithEmail() {
			this.selectedEmployees = this.personnelWithEmail.slice();
		},
		openConfirmDialog() {
			this.confirmError = "";
			this.confirmDialog = true;
		},
		applyConfirmationRecipients(list) {
			if (Array.isArray(list) && list.length) {
				this.confirmationRecipients = list;
			}
		},
		selectActivity(item) {
			var row = item && item.id ? item : item && item.item ? item.item : null;
			if (!row || !row.id) return;
			if (this.selectedPlanningId === row.id) return;
			this.selectedPlanningId = row.id;
			this.selectedPlanning = row;
			this.audience = {
				for_personnel: row.for_personnel,
				for_student: row.for_student
			};
			this.personnel = [];
			this.selectedEmployees = [];
			this.subject = "";
			this.previewHtml = "";
			this.sendResult = null;
			this.selectedConfirmationEmails = [];
			this.confirmError = "";
			this.fetchCompose(row.id);
		},
		fetchSetup() {
			var self = this;
			var userId = this.currentUserId();
			if (!userId) {
				this.loadError = "U bent niet ingelogd.";
				return;
			}
			this.loading = true;
			this.loadError = "";
			ax.get("/communication/setup", { params: { user_id: userId } })
				.then(function(res) {
					var data = res.data || {};
					self.schoolyear = data.schoolyear || null;
					self.activities = Array.isArray(data.activities) ? data.activities : [];
					self.applyConfirmationRecipients(data.confirmation_recipients);
				})
				.catch(function(err) {
					self.schoolyear = null;
					self.activities = [];
					self.loadError =
						(err && err.response && err.response.data && err.response.data.msg) ||
						"Communicatiegegevens konden niet worden geladen.";
				})
				.finally(function() {
					self.loading = false;
				});
		},
		fetchCompose(planningId) {
			var self = this;
			var userId = this.currentUserId();
			if (!userId) {
				this.loadError = "U bent niet ingelogd.";
				return;
			}
			this.composeLoading = true;
			this.loadError = "";
			ax.get("/communication/compose/" + planningId, { params: { user_id: userId } })
				.then(function(res) {
					var data = res.data || {};
					self.selectedPlanning = data.planning || null;
					self.audience = data.audience || null;
					self.personnel = Array.isArray(data.personnel) ? data.personnel : [];
					self.subject = data.subject || "";
					self.previewHtml = data.preview_html || "";
					self.selectedEmployees = [];
					self.applyConfirmationRecipients(data.confirmation_recipients);
				})
				.catch(function(err) {
					self.personnel = [];
					self.selectedEmployees = [];
					self.subject = "";
					self.previewHtml = "";
					self.loadError =
						(err && err.response && err.response.data && err.response.data.msg) ||
						"Activiteit kon niet worden geladen.";
				})
				.finally(function() {
					self.composeLoading = false;
				});
		},
		sendEmails() {
			var self = this;
			var userId = this.currentUserId();
			if (!userId || !this.selectedPlanning) return;
			if (!this.selectedConfirmationEmails.length) {
				this.confirmError = "Selecteer minstens één adres voor de bevestigingssamenvatting.";
				return;
			}
			this.sending = true;
			this.loadError = "";
			this.confirmError = "";
			this.sendResult = null;
			ax.post("/communication/send", {
				user_id: userId,
				planning_id: this.selectedPlanning.id,
				employee_ids: this.selectedEmployees.map(function(person) {
					return person.id;
				}),
				confirmation_emails: this.selectedConfirmationEmails.slice()
			})
				.then(function(res) {
					self.sendResult = res.data || { success: true, msg: "E-mails zijn verstuurd" };
					self.confirmDialog = false;
					self.selectedEmployees = [];
				})
				.catch(function(err) {
					var msg =
						(err && err.response && err.response.data && err.response.data.msg) ||
						"E-mails konden niet worden verstuurd.";
					if (err && err.response && err.response.status === 400) {
						self.confirmError = msg;
						return;
					}
					self.sendResult = null;
					self.loadError = msg;
					self.confirmDialog = false;
				})
				.finally(function() {
					self.sending = false;
				});
		}
	}
};
</script>

<style scoped>
.activity-swatch {
	display: inline-block;
	width: 28px;
	height: 18px;
	border-radius: 2px;
	vertical-align: middle;
}
.selected-activity-card {
	background: #fff8e1;
}
.email-preview-frame {
	width: 100%;
	height: 420px;
	border: 0;
	border-top: 1px solid #eeeeee;
	background: #ffffff;
}
.email-preview-frame--dialog {
	height: 320px;
	border: 1px solid #eeeeee;
}
</style>
